/**
 * ESG Schemas
 *
 * Zod schemas for validating ESG metrics and form-related API responses.
 */

import { z } from 'zod';

// Option schema
export const OptionSchema = z.object({
  code: z.string(),
  label: z.string(),
});

export type Option = z.infer<typeof OptionSchema>;

// Option set schema
export const OptionSetSchema = z.object({
  id: z.number(),
  name: z.string(),
  options: z.array(OptionSchema),
});

export type OptionSet = z.infer<typeof OptionSetSchema>;

// Category reference schema
export const CategoryRefSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export type CategoryRef = z.infer<typeof CategoryRefSchema>;

// Subcategory reference schema
export const SubcategoryRefSchema = z.object({
  id: z.number(),
  name: z.string(),
  category_id: z.number(),
  category_name: z.string(),
});

export type SubcategoryRef = z.infer<typeof SubcategoryRefSchema>;

// Metric type schema
export const MetricTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  form_question: z.string(),
  form_question_description: z.string().optional(),
  form_question_type: z.string(),
  value_kind: z.enum(['boolean', 'categorical', 'numeric', 'text']),
  required: z.boolean(),
  default_unit: z.string().nullable(),
  evidence_types: z.array(z.string()),
  description: z.string().optional(),
  option_set: OptionSetSchema.nullable(),
  relevant_categories: z.array(CategoryRefSchema).optional(),
  relevant_subcategories: z.array(SubcategoryRefSchema).optional(),
});

export type MetricType = z.infer<typeof MetricTypeSchema>;

// Subcategory group schema
export const SubcategoryGroupSchema = z.object({
  id: z.number().nullable(),
  name: z.string(),
  subcategory_count: z.number(),
  metrics: z.array(MetricTypeSchema),
});

export type SubcategoryGroup = z.infer<typeof SubcategoryGroupSchema>;

// ESG section schema
export const ESGSectionSchema = z.object({
  id: z.number(),
  name: z.string(),
  groups: z.array(SubcategoryGroupSchema),
});

export type ESGSection = z.infer<typeof ESGSectionSchema>;

// Form fields response schema
export const FormFieldsResponseSchema = z.object({
  sections: z.array(ESGSectionSchema),
});

export type FormFieldsResponse = z.infer<typeof FormFieldsResponseSchema>;

// Subcategory schema
export const SubcategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  category: z.string(),
  category_id: z.number(),
});

export type Subcategory = z.infer<typeof SubcategorySchema>;

// Subcategories response
export const SubcategoriesResponseSchema = z.object({
  subcategories: z.array(SubcategorySchema),
});

export type SubcategoriesResponse = z.infer<typeof SubcategoriesResponseSchema>;

// Questionnaire claim response
export const ClaimResponseSchema = z.object({
  id: z.number(),
  metric_type_id: z.number(),
  value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
  categories: z.array(z.number()).optional(),
  subcategories: z.array(z.number()).optional(),
  products: z.array(z.number()).optional(),
  confidence_band: z.string().optional(),
  notes: z.string().optional(),
});

export type ClaimResponse = z.infer<typeof ClaimResponseSchema>;

// Draft questionnaire schema
export const DraftQuestionnaireSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  status: z.enum(['submitted', 'master', 'approved', 'rejected']),
  organisation_id: z.number(),
  user_id: z.number(),
});

export type DraftQuestionnaire = z.infer<typeof DraftQuestionnaireSchema>;

// Draft questionnaires response
export const DraftQuestionnairesResponseSchema = z.object({
  questionnaires: z.array(DraftQuestionnaireSchema),
});

export type DraftQuestionnairesResponse = z.infer<typeof DraftQuestionnairesResponseSchema>;

// Last questionnaire response
export const LastQuestionnaireResponseSchema = z.object({
  questionnaire_id: z.number().nullable(),
  claims: z.array(ClaimResponseSchema).optional(),
  organisation_data: z.record(z.string(), z.unknown()).optional(),
});

export type LastQuestionnaireResponse = z.infer<typeof LastQuestionnaireResponseSchema>;

// Submit questionnaire request
export const SubmitQuestionnaireRequestSchema = z.object({
  organisation_id: z.number(),
  user_id: z.number(),
  claims: z.array(z.object({
    metric_type_id: z.number(),
    value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
    categories: z.array(z.number()).optional(),
    subcategories: z.array(z.number()).optional(),
    products: z.array(z.number()).optional(),
    confidence_band: z.string().optional(),
    notes: z.string().optional(),
  })),
  is_draft: z.boolean().optional(),
});

export type SubmitQuestionnaireRequest = z.infer<typeof SubmitQuestionnaireRequestSchema>;

// Submit questionnaire response
export const SubmitQuestionnaireResponseSchema = z.object({
  questionnaire_id: z.number(),
  status: z.string(),
  message: z.string().optional(),
});

export type SubmitQuestionnaireResponse = z.infer<typeof SubmitQuestionnaireResponseSchema>;
